import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { createNomination, type Nomination, type NominationCreate } from '../lib/api';
import { WbdFieldInput } from './TermPickers';

/** The editable fields, in display order. */
const FIELDS: { key: keyof NominationCreate; label: string; multiline?: boolean }[] = [
  { key: 'nomination_code', label: 'Project code' },
  { key: 'year', label: 'Year' },
  { key: 'category_name', label: 'Category' },
  { key: 'project_title', label: 'Project title' },
  { key: 'project_city', label: 'City' },
  { key: 'project_country', label: 'Country' },
  { key: 'architect_company_name', label: 'Architect company' },
  { key: 'architect_company_country', label: 'Architect company country' },
  { key: 'lead_architect_name', label: 'Lead architect' },
  { key: 'client_name', label: 'Client' },
  { key: 'google_maps_link', label: 'Google Maps link' },
  { key: 'project_completion_date', label: 'Completion date' },
  { key: 'project_summary', label: 'Project summary', multiline: true },
];

function fromSource(src: Nomination | null): NominationCreate {
  if (!src) return {};
  return {
    nomination_code: src.nomination_code,
    year: src.year,
    category_name: src.category_name,
    category_status: src.category_status,
    project_title: src.project_title,
    project_city: src.project_city,
    project_country: src.project_country,
    architect_company_name: src.architect_company_name,
    architect_company_country: src.architect_company_country,
    lead_architect_name: src.lead_architect_name,
    client_name: src.client_name,
    google_maps_link: src.google_maps_link,
    project_completion_date: src.project_completion_date,
    project_summary: src.project_summary,
  };
}

interface Props {
  open: boolean;
  /** When set, this is a duplicate: media + architect grants are copied from it. */
  source: Nomination | null;
  onClose: () => void;
  onCreated: (created: Nomination) => void;
}

/**
 * Create a new entry, or duplicate an existing one. For a duplicate the form is
 * pre-filled from the source and a new code is suggested; on save, the backend
 * also copies the source's images and architect grants.
 */
export function EntryFormDialog({ open, source, onClose, onCreated }: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isDuplicate = source != null;

  const [form, setForm] = useState<NominationCreate>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const base = fromSource(source);
    // Suggest a fresh code for a duplicate so it stays unique.
    if (source?.nomination_code) base.nomination_code = `${source.nomination_code}-2`;
    setForm(base);
    setError(null);
  }, [open, source]);

  const set = (key: keyof NominationCreate, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    if (!form.nomination_code || !form.nomination_code.trim()) {
      setError('A project code is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await createNomination({
        ...form,
        duplicate_from: source?.nomination_uuid ?? null,
      });
      onCreated(created);
    } catch (err) {
      // Surface the backend's duplicate-code message when present.
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err instanceof Error ? err.message : 'Create failed');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>{isDuplicate ? 'Duplicate entry' : 'Add entry'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {isDuplicate && (
            <Typography variant="body2" color="text.secondary">
              Copying images and architect access from{' '}
              <strong>{source?.project_title || source?.nomination_code}</strong>. Change the
              category (and code) for the new entry, then create.
            </Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {FIELDS.map((f) => (
            <WbdFieldInput
              key={f.key}
              field={f.key}
              label={f.label}
              multiline={f.multiline}
              value={(form[f.key] as string | undefined) ?? ''}
              onChange={(v) => set(f.key, v)}
              disabled={saving}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={() => void submit()} disabled={saving}>
          {saving ? 'Creating…' : isDuplicate ? 'Create duplicate' : 'Create entry'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
